import { DialogTitle } from '@mui/material';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useToast } from '@retail/hooks';
import {
  AddItemToAssortmentDialogContent,
  AddToAssortmentFunction,
} from '@retail/my-assortment/components';
import { useAssortmentItemsActions } from '@retail/my-assortment/context';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentItemBase } from '@retail/my-assortment/types';
import { ItemPriority } from '@retail/products/types';
import { Dialog } from '@shared/components';
import { useCallback } from 'react';
import { withDialogSuspenseErrorBoundary } from '@retail/components';

interface Props {
  open: boolean;
  item: Pick<AssortmentItemBase, 'supplier' | 'mgItemNumber' | 'priority' | 'isStocked'>;
  onClose: () => void;
}

function EditAssortmentItemDialog({ open, item, onClose }: Props) {
  const t = useMyAssortmentTFunction();
  const toast = useToast();
  const { isBrandContext } = useSelectedOrgUnit();

  const { addItemsToAssortment } = useAssortmentItemsActions();
  const showStockedStatus = !isBrandContext && item.isStocked !== undefined;

  const handleEditItem: AddToAssortmentFunction = useCallback(
    ({ fromDate, toDate, priority, isStocked }) => {
      addItemsToAssortment(
        [
          {
            mgSupplierId: item.supplier.id,
            mgItemNumber: item.mgItemNumber,
            priority,
            isStocked: showStockedStatus ? isStocked : undefined,
            validFrom: fromDate.toISOString(),
            validTo: toDate ? toDate.toISOString() : undefined,
          },
        ],
        { skipToast: true }
      )
        .then(() => {
          toast.success(t('myAssortment.actions.itemUpdated'));
          onClose();
        })
        .catch(() => toast.error(t('myAssortment.actions.genericActionError')));
    },
    [item, showStockedStatus, addItemsToAssortment, onClose, toast, t]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('myAssortment.actions.editItem')}</DialogTitle>
      <AddItemToAssortmentDialogContent
        supplierId={item.supplier.id}
        defaultPriority={item.priority.prioritised ? ItemPriority.TEN : ItemPriority.ZERO}
        defaultToDate={item.priority.validTo}
        defaultIsStocked={item.isStocked}
        showStockedStatus={showStockedStatus}
        submitLabel={t('myAssortment.save')}
        handleAddItem={handleEditItem}
        onClose={onClose}
      />
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(EditAssortmentItemDialog);
